import type { RowData } from "@tanstack/react-table";
import { getDisplayColumns, isNavigableColumn } from "../focus/navigation";
import type { DataGridCellChange } from "../types";
import { getEditableCell } from "./cellAccess";
import {
    createCellLookup,
    getActiveRect,
    getColumnIndex,
    getRectCells,
    selectRect,
} from "./cellRect";
import { getCellCopyText, parseCellChange } from "./cellText";
import { parseTsv, tileClip, toHtml, toTsv } from "./clipboard";
import { type EditContext, clearRange, emitChanges } from "./editActions";

/** The range (or active cell) as TSV and HTML; `null` without one. */
export const copyRange = <TData extends RowData>({
    instance,
}: EditContext<TData>) => {
    const { table } = instance;
    const rect = getActiveRect(table);
    if (!rect) {
        return null;
    }
    const copyRows = getRectCells({ table, rect }).map((cells) =>
        cells.map(getCellCopyText),
    );
    return { tsv: toTsv(copyRows), html: toHtml(copyRows) };
};

/** Copies, then clears the editable cells. */
export const cutRange = <TData extends RowData>(
    context: EditContext<TData>,
) => {
    const copied = copyRange(context);
    if (copied) {
        clearRange(context);
    }
    return copied;
};

/**
 * Pastes TSV at the range's top-left, tiled across the range when it is a
 * whole multiple of the clip. Each value goes through the target column's
 * editor; read-only cells and values that don't parse are skipped. The pasted
 * area becomes the selection.
 */
export const pasteText = <TData extends RowData>(
    context: EditContext<TData>,
    text: string,
) => {
    const { instance } = context;
    const { table } = instance;
    const rect = getActiveRect(table);
    if (!rect) {
        return false;
    }
    const getCellAt = createCellLookup(table);
    const columns = getDisplayColumns(table)
        .slice(rect.left)
        .filter((column) => isNavigableColumn(column.columnDef));
    const rangeColumnCount = columns.filter(
        (column) => getColumnIndex(table, column.id) <= rect.right,
    ).length;
    const tiledRows = tileClip({
        clip: parseTsv(text),
        rowCount: rect.bottom - rect.top + 1,
        columnCount: Math.max(rangeColumnCount, 1),
    });
    const changes: DataGridCellChange[] = [];
    let lastColumnIndex = rect.left;
    let lastRowIndex = rect.top;
    tiledRows.forEach((texts, rowOffset) => {
        const rowIndex = rect.top + rowOffset;
        texts.forEach((cellText, columnOffset) => {
            const column = columns[columnOffset];
            const columnIndex = column ? getColumnIndex(table, column.id) : -1;
            const cell = getCellAt({ rowIndex, columnIndex });
            if (!cell) {
                return;
            }
            lastRowIndex = Math.max(lastRowIndex, rowIndex);
            lastColumnIndex = Math.max(lastColumnIndex, columnIndex);
            const editable = getEditableCell(instance, cell);
            const change = editable
                ? parseCellChange({ instance, editable, text: cellText })
                : null;
            if (change) {
                changes.push(change);
            }
        });
    });
    emitChanges(context, changes);
    selectRect({
        table,
        rect: { ...rect, bottom: lastRowIndex, right: lastColumnIndex },
    });
    return true;
};
